// combatpro/panels/monsters/studio/useMonsterStudioLibrary.js
//
// Combat PRO - Monster Studio Library runtime (Stage 57: Library Runtime Foundation).
//
// The library runtime for Monster Studio, expressed as a React hook. It OWNS the
// library behaviour and state - the active section, search query, filter state,
// active collection, and the custom-monster pool - and produces the rendered
// library list, the collection chips, and the hero counts. It loads a chosen
// monster into the editor via the editor runtime's loadModel seam (passed in as
// onLoad); it never edits model data itself. It is NOT CombatContext and performs
// no persistence, cloud, save, or DOM work.
//
// All model transformation is delegated to the pure Stage 55 engine
// (officialToStudioModel / cloneStudioMonster / nextMonsterId). Editing stays with
// the Stage 56 editor runtime. This runtime never recreates either layer's logic;
// it owns only library list generation, filtering, and load orchestration.
//
// Faithful to the legacy library (battletracker.html):
//   - sections / list  <- viStudioRenderLibrary (11173): official from the runtime
//                         monster DB (window.VI_MONSTERS_DB, legacy MONSTERS_DB);
//                         custom from the pool with favourites / archived / recent
//                         (last-saved desc, 20) / mine (non-archived) selection.
//   - search + filters <- the same combined predicate (name / tag / collection text
//                         search; type substring; CR exact; size exact; alignment
//                         substring; tag substring). Cap 300 with a "(showing 300)"
//                         note and the count header / empty message.
//   - collections      <- viStudioAllCollections (11455) + viStudioToggleCollection
//                         (11468): chips with counts; toggling a collection while in
//                         the official section switches to 'mine'.
//   - load             <- viStudioPick (11211): official -> officialToStudioModel;
//                         custom -> cloneStudioMonster; dirty cleared by the editor.
//
// NOTE (legacy inconsistency, preserved): the sidebar exposes an Environment filter
// (#vi-st-f-env), but viStudioRenderLibrary never applies it. This runtime keeps the
// Environment value as controlled state but does NOT filter on it, matching legacy.
//
// NOTE (deferred data): the custom pool is cloud-loaded in the legacy
// (viStudioLoadLibrary). That is persistence and belongs to a later stage, so the
// pool starts empty here and is populated through the setCustomMonsters seam later.
// Official monsters (a static bundled DB) are available immediately.
//
// No DOM, no querySelector, no innerHTML, no fetch, no localStorage, no backend.
// No optional chaining. No em dash.

import { useState } from 'react';
import {
  officialToStudioModel,
  cloneStudioMonster,
  nextMonsterId
} from '../../../monster/monsterStudioEngine';

// ---- Pure list helpers (library list generation) ---------------------------

function buildOfficialItems(officialDb) {
  return officialDb.map(function(o) {
    return {
      kind: 'official',
      name: o.name,
      sub: (o.race || o.type || '') + (o.cr != null ? (' \u2022 CR ' + o.cr) : ''),
      key: (o.customMonsterId || o.id || o.name),
      size: o.size, cr: o.cr, type: (o.race || o.type || ''), align: o.alignment,
      tags: [], collections: []
    };
  });
}

function buildCustomItems(pool, section) {
  var list = Array.isArray(pool) ? pool : [];
  var src;
  if (section === 'favourites') { src = list.filter(function(m) { return m.favourite; }); }
  else if (section === 'archived') { src = list.filter(function(m) { return m.archived; }); }
  else if (section === 'recent') { src = list.slice().sort(function(a, b) { return (b.lastSaved || 0) - (a.lastSaved || 0); }).slice(0, 20); }
  else { src = list.filter(function(m) { return !m.archived; }); }
  return src.map(function(m) {
    return {
      kind: 'custom',
      name: m.customName || m.name,
      sub: (m.type || m.race || '') +
        (m.cr ? (' \u2022 CR ' + m.cr) : '') +
        ((m.tags && m.tags.length) ? (' \u2022 \ud83c\udff7 ' + m.tags.length) : '') +
        (m.favourite ? ' \u2022 \u2b50' : ''),
      key: (m.customMonsterId || m.id || m.name),
      size: m.size, cr: m.cr, type: (m.type || m.race || ''), align: m.alignment,
      tags: (m.tags || []), collections: (m.collections || [])
    };
  });
}

function applyFilters(items, query, filters, collection) {
  var q = (query || '').toLowerCase();
  var fType = (filters.type || '').trim();
  var fCr = (filters.cr || '').trim();
  var fSize = (filters.size || '').trim();
  var fAlign = (filters.align || '').trim();
  var fTag = (filters.tag || '').trim().toLowerCase();
  return items.filter(function(it) {
    var tagStr = (it.tags || []).join(' ').toLowerCase();
    var colStr = (it.collections || []).join(' ').toLowerCase();
    if (q && it.name.toLowerCase().indexOf(q) === -1 && tagStr.indexOf(q) === -1 && colStr.indexOf(q) === -1) { return false; }
    if (fType && String(it.type || '').toLowerCase().indexOf(fType.toLowerCase()) === -1) { return false; }
    if (fCr && String(it.cr) !== String(fCr)) { return false; }
    if (fSize && String(it.size) !== fSize) { return false; }
    if (fAlign && String(it.align || '').toLowerCase().indexOf(fAlign.toLowerCase()) === -1) { return false; }
    if (fTag && tagStr.indexOf(fTag) === -1) { return false; }
    // Environment filter (filters.env) intentionally not applied - see header note.
    if (collection && (it.collections || []).indexOf(collection) === -1) { return false; }
    return true;
  });
}

function buildCollections(pool) {
  var set = {};
  (Array.isArray(pool) ? pool : []).forEach(function(m) {
    (m.collections || []).forEach(function(c) { if (c) { set[c] = (set[c] || 0) + 1; } });
  });
  return Object.keys(set).sort().map(function(c) { return { name: c, count: set[c] }; });
}

var DEFAULT_FILTERS = { type: '', cr: '', size: '', align: '', env: '', tag: '' };

// ---- Hook ------------------------------------------------------------------

function useMonsterStudioLibrary(props) {
  const onLoad = (props && props.onLoad) ? props.onLoad : function() {};
  const [activeSection, setActiveSection] = useState('official');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState(function() { return Object.assign({}, DEFAULT_FILTERS); });
  const [activeCollection, setActiveCollection] = useState('');
  const [customMonsters, setCustomMonsters] = useState([]);

  var officialDb = (typeof window !== 'undefined' && Array.isArray(window.VI_MONSTERS_DB)) ? window.VI_MONSTERS_DB : [];

  // Library list generation (the React-rendered equivalent of viStudioRenderLibrary).
  var rawItems = (activeSection === 'official')
    ? buildOfficialItems(officialDb)
    : buildCustomItems(customMonsters, activeSection);
  var filtered = applyFilters(rawItems, searchQuery, filters, activeCollection);
  var count = filtered.length;
  var shown = (count > 300) ? filtered.slice(0, 300) : filtered;

  var collections = buildCollections(customMonsters);

  function setSection(sec) {
    setActiveSection(sec);
  }

  function setSearch(q) {
    setSearchQuery(q);
  }

  function setFilter(key, value) {
    setFilters(function(prev) {
      var next = Object.assign({}, prev);
      next[key] = value;
      return next;
    });
  }

  function toggleCollection(name) {
    var nextCollection = (activeCollection === name) ? '' : name;
    setActiveCollection(nextCollection);
    if (nextCollection && activeSection === 'official') {
      setActiveSection('mine');
    }
  }

  // Load a library item into the editor (viStudioPick). Official -> a fresh studio
  // model (id minted from the current pool); custom -> a deep clone of the pooled
  // monster. The editor's loadModel applies it and clears dirty.
  function load(kind, key) {
    if (kind === 'official') {
      var o = officialDb.find(function(m) { return m.name === key; });
      if (o) {
        var id = nextMonsterId(customMonsters, 0).id;
        onLoad(officialToStudioModel(o, id));
      }
    } else {
      var found = customMonsters.find(function(x) {
        return (x.customMonsterId || x.id || x.customName || x.name) === key;
      });
      if (found) {
        onLoad(cloneStudioMonster(found));
      }
    }
  }

  return {
    activeSection: activeSection,
    searchQuery: searchQuery,
    filters: filters,
    activeCollection: activeCollection,
    collections: collections,
    items: { count: count, shown: shown, capped: count > 300 },
    officialCount: officialDb.length,
    customCount: customMonsters.length,
    setSection: setSection,
    setSearch: setSearch,
    setFilter: setFilter,
    toggleCollection: toggleCollection,
    setCustomMonsters: setCustomMonsters,
    load: load
  };
}

export default useMonsterStudioLibrary;
